import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Wallet, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { InvoiceGroupedList } from "@/components/invoices/InvoiceGroupedList";

interface Invoice {
  id: string;
  payment_amount: number;
  payment_due_date: string;
  supplier_name: string;
  supplier_email: string;
  status: string;
  discount_amount: number | null;
  discounted_payment_amount: number | null;
  created_at: string;
}

interface BudgetDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  totalBudget: number;
  usedBudget: number;
  availableBudget: number;
  invoices: Invoice[];
  budgetPeriodStart?: string;
  budgetPeriodEnd?: string;
}

const budgetRefreshHistory = [
  { date: new Date(2026, 1, 1), amount: 100000, note: "Monthly auto-refresh" },
  { date: new Date(2026, 0, 1), amount: 100000, note: "Monthly auto-refresh" },
  { date: new Date(2025, 11, 15), amount: 50000, note: "Manual top-up" },
  { date: new Date(2025, 11, 1), amount: 100000, note: "Monthly auto-refresh" },
];

export function BudgetDetailModal({
  open,
  onOpenChange,
  totalBudget,
  usedBudget,
  availableBudget,
  invoices,
  budgetPeriodStart,
  budgetPeriodEnd,
}: BudgetDetailModalProps) {
  const acceleratedInvoices = invoices.filter(
    (inv) => inv.status === "early_payment_scheduled" || inv.status === "early_payment_completed"
  );

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const usedPercent = totalBudget > 0 ? Math.min((usedBudget / totalBudget) * 100, 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-primary" />
            Budget Overview
          </DialogTitle>
          <DialogDescription>
            {budgetPeriodStart && budgetPeriodEnd
              ? `Period: ${format(new Date(budgetPeriodStart), "MMM d")} – ${format(new Date(budgetPeriodEnd), "MMM d, yyyy")}`
              : "Current budget period"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Budget bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Used:{" "}
                <span className="font-semibold text-foreground">{formatCurrency(usedBudget)}</span>
              </span>
              <span className="text-muted-foreground">
                Total:{" "}
                <span className="font-semibold text-foreground">{formatCurrency(totalBudget)}</span>
              </span>
            </div>
            <div className="h-3 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${usedPercent}%`,
                  background:
                    usedPercent > 90
                      ? "hsl(var(--destructive))"
                      : usedPercent > 70
                        ? "hsl(var(--warning))"
                        : "hsl(var(--success))",
                }}
              />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{usedPercent.toFixed(0)}% used</span>
              <span className="font-semibold text-success">
                {formatCurrency(availableBudget)} available
              </span>
            </div>
          </div>

          {/* Accelerated invoices consuming budget */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-2">
              Accelerated Invoices ({acceleratedInvoices.length})
            </h3>
            {acceleratedInvoices.length > 0 ? (
              <InvoiceGroupedList
                invoices={acceleratedInvoices}
                emptyMessage="No accelerated invoices"
                maxHeight="200px"
              />
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No invoices currently using the budget
              </p>
            )}
          </div>

          {/* Budget refresh history */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5 text-primary" />
              Budget Refresh History
            </h3>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {budgetRefreshHistory.map((entry, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-2.5 bg-muted rounded-lg"
                >
                  <div>
                    <p className="text-xs font-medium text-foreground">{entry.note}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {format(entry.date, "MMM d, yyyy")}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    +{formatCurrency(entry.amount)}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
